import { Injectable, inject } from "@angular/core";
import { ENVIRONMENT } from "../../environments/environment";
import { EnergyConsult } from "../classes/flow/request/EnergyConsult";
import { RequestState, RequestStates } from "../classes/flow/request/RequestStates";
import { EpfService } from "./epf.service";

@Injectable({
  providedIn: "root",
})
export class EnergyConsultService {
  private epf = inject(EpfService);

  public async ckdEC(ec: EnergyConsult) {
    return this.epf.ckd(ec);
  }

  /**
   *
   * @param requests
   * @returns requests filters by allowed specialties
   */
  public filterRequestsByAllowedSpecialties(requests: EnergyConsult[]) {
    return requests.filter((request) => ENVIRONMENT.ALLOWED_SPECIALTIES.includes(request.specialty.id));
  }

  /**
   * Get all requests
   * @returns An array with requests
   */
  public async sendPDFToResident(energyConsultId: number) {
    return this.epf.sendPdfToResident(energyConsultId);
  }

  /**
   * Sets the state of the energyConsult to done
   * @param energyConsult The id of the energyConsult
   */
  public async setState(energyConsult: EnergyConsult, state: RequestStates, states: RequestState[]) {
    try {
      const payload = energyConsult.duplicate();
      payload.setState(state, states);

      const result = await this.epf.updateEnergyConsult(payload);
      return result;
    } catch (error) {
      console.error(error);
      return energyConsult;
    }
  }

  /**
   * Disapproves the given energyConsult
   * @param energyConsult The energyConsult to disapprove
   */
  public async disapproveEnergyConsult(energyConsult: EnergyConsult, reason: string, requestStates: RequestState[]) {
    try {
      const payload = energyConsult.duplicate();
      payload.setState(RequestStates.DISAPPROVED, requestStates);
      payload.disapprovalReason = reason;

      const result = await this.epf.updateEnergyConsult(payload);
      return result;
    } catch (error) {
      console.error(error);
      return energyConsult;
    }
  }

  /**
   * Deletes a specific energyConsult
   * @param energyConsult The energyConsult
   */
  public async delete(energyConsult: EnergyConsult) {
    return this.epf.deleteEnergyConsult(energyConsult);
  }

  public async getAllConsultsCSV(
    options: Partial<{
      requestStateIDS: number[] | null;
      dateFrom: Date | null;
      dateTo?: Date | null;
      actionsLinkedToEC?: { id: number; aaID: number }[];
    }>,
  ): Promise<string> {
    if (options.dateTo) {
      options.dateTo.setHours(23, 59, 59, 999);
    }

    const result = await this.epf.getConsultsAsCSV(options);

    return result.content;
  }

  public async getConsultsByCoachByDay(allConsults: EnergyConsult[], coachIds: number[], date: Date): Promise<EnergyConsult[]> {
    const UTCDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayBefore = new Date(UTCDate);
    const dayAfter = new Date(UTCDate);
    dayAfter.setUTCDate(dayAfter.getUTCDate() + 1);
    
    return allConsults.filter((x) => {
      const appointmentDate = x.appointmentDate ? new Date(x.appointmentDate) : null;
      return x.coach?.id && coachIds.includes(x.coach.id) && appointmentDate && appointmentDate > dayBefore && appointmentDate < dayAfter;
    });
  }
}
